import React, { useEffect } from 'react';
import { clone, partial, times } from 'lodash';
import useTaskField from '@dyl/pages/Tasks/edit/useTaskField';
import RubricBlock from '@dyl/blocks/RubricBlock';
import { Divider, Grid, Typography, MenuItem, Select, FormHelperText, FormControl } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import AudioAndTranscriptBlock from "@dyl/blocks/AudioAndTranscriptBlock";

import { Checkbox } from "@mui/material";
import ImagePicker from "@dyl/components/ImagePicker";
import { useCallback } from "react";
import {
    CheckCircle as CircleChecked,
    RadioButtonUnchecked as CircleUnchecked,
} from '@mui/icons-material';
import indexToLetter from "@dyl/utils/indexToLetter";
import { useImage } from "@dyl/systems/ImagesManager";
import DragCanvas, { DragCanvasImage } from "@dyl/components/DragCanvas";
import PreviousImages from "@dyl/task-types/sharedLogic/SpeakingGenericEditor/utils/PreviousImages";
import SectionDividers from "@dyl/components/SectionDividers";
import { AnswerDraggable } from "@dyl/task-types/sharedLogic/SpeakingGenericEditor/PlacementEditor";
import ColourPicker from "@dyl/components/ColourPicker";
import useChangeEvent from "@dyl/hooks/useChangeEvent";

const useStyles = makeStyles(theme => ({
    // root: {
    //     maxWidth: theme.breakpoints.values.md,
    // },
    pickersContainer: {
        maxWidth: theme.breakpoints.values.md,
    },
}));



export default function PictureEditingEditor() {
    const classes = useStyles();
    const [items] = useTaskField('items');

    return (
        <div className={classes.root}>
            <SectionDividers>
                {times(items.length, i => <Item key={i} index={i} phase={items[i].phase}/>)}
            </SectionDividers>
        </div>
    );
}

function Item({ index, phase = 'PHASE_1' }) {
    const useField = partial(useTaskField, 'items', index);
    const [isExample] = useField('is_example');
    const [audioId, setAudioId, audioError] = useField('audio_id');

    const [items] = useTaskField('items');
    const examplesUntilNow = items
        .slice(0, index)
        .filter(x => x.is_example)
        .reduce(x => x + 1, 0);

    return (
        <>
            <Typography variant='h6'>
                { isExample ? `Example ${examplesUntilNow + 1}` : `Item ${index + 1 - examplesUntilNow}`}
            </Typography>

            <Grid container spacing={3}>
                <Grid item xs={12} sm={8}>

                    {isExample && (
                        <Grid item xs={12}>
                            <RubricBlock phase={phase} index={index}/>
                        </Grid>
                    )}

                    <AudioAndTranscriptBlock
                        value={audioId}
                        onChange={id => setAudioId(id)}
                        label='Stimulus Audio'
                        error={audioError}
                        required
                    />

                    {isExample && phase === 'PHASE_2' && <PaletteSelector />}

                </Grid>
                {phase === 'PHASE_1'
                    ? <Phase1PlacementEditor index={index} />
                    : <Phase2PlacementEditor index={index} />
                }
            </Grid>
        </>
    );
}

function Phase1PlacementEditor(
    {
        index,
        draggable = true,
        backgroundImagePath = 'background_image',
    }) {
    const [backgroundImageId] = useTaskField(backgroundImagePath);
    const { src: backgroundImageSrc } = useImage(backgroundImageId);

    const [answers] = useTaskField('items', index, 'answers');

    return (
        <Grid item xs={12} sm={8} md={6}>
            <ItemImagePicker index={index}/>
            <Divider style={{
                marginTop: 4,
                marginBottom: 6,
            }}>
            </Divider>
            <DragCanvas backgroundSrc={backgroundImageSrc}>
                <PreviousImages index={index} />
                {times(answers.length, answerIndex => (
                    <AnswerDraggableBoundingBox
                        key={answerIndex}
                        index={answerIndex}
                        itemIndex={index}
                        disabled={!draggable}
                        imagePath={['items', index, 'image_id']}
                    />
                ))}
            </DragCanvas>
        </Grid>
    );
}

function Phase2PlacementEditor(
    {
        index,
        draggable = true,
        backgroundImagePath = 'background_image',
    }) {
    const classes = useStyles();
    const [backgroundImageId] = useTaskField(backgroundImagePath);
    const { src: backgroundImageSrc } = useImage(backgroundImageId);

    const [answers] = useTaskField('items', index, 'answers');
    // Passing colors as a prop to trigger updates on state change
    const [colors] = useTaskField('colors');

    return (
        <>
            <Grid container spacing={2} className={classes.pickersContainer}>
                <Grid item xs={12} sm={6} md={4} >
                    <ColorKeySelector colors={colors} itemIndex={index} answerIndex={0}/>
                </Grid>
                {times(answers.length, answerIndex => (
                    <Grid item xs={12} sm={6} md={4} key={answerIndex}>
                        <AnswerImagePickers
                            index={answerIndex}
                            itemIndex={index}
                            hasKeySelection={false}
                        />
                    </Grid>
                ))}
            </Grid>
            <Grid item xs={12} sm={12}>
                <DragCanvas backgroundSrc={backgroundImageSrc}>
                    {times(answers.length, answerIndex => (
                        <AnswerDraggable
                            key={answerIndex}
                            index={answerIndex}
                            itemIndex={index}
                            disabled={!draggable}
                            imageFieldName={'image_id'}
                        />
                    ))}
                </DragCanvas>
            </Grid>
        </>
    );
}

function AnswerDraggableBoundingBox({ index, itemIndex, disabled = false, imagePath = null }) {
    imagePath = imagePath || ['items', itemIndex, 'answers', index, 'image_id'];
    const useField = partial(useTaskField, 'items', itemIndex, 'answers', index);
    const [imageId] = useTaskField(...imagePath);
    const [isCorrectAnswer] = useField('is_correct_answer');
    const { src } = useImage(imageId);

    const [coordinates, setCoordinates] = useField('coordinates');

    return (
        <DragCanvasImage
            text={indexToLetter(index)}
            disabled={disabled}
            src={src}
            alt={imageId}
            coordinates={coordinates}
            onCoordinatesChange={setCoordinates}
            imageStyle = {{
                opacity: 0.45,
                outlineColor: isCorrectAnswer ? 'green' : 'red',
                outlineStyle: 'dashed',
                outlineWidth: '4px'
            }}
        />
    );
}

function ItemImagePicker({ index }) {
    const [imageId, setImageId, imageError] = useTaskField('items', index, 'image_id');

    return (
        <ImagePicker
            label={`Placement Image`}
            value={imageId}
            onChange={setImageId}
            fullWidth
            error={!!imageError}
        />
    );
}

function AnswerImagePickers({ index, itemIndex, hasKeySelection= false }) {
    const useField = partial(useTaskField, 'items', itemIndex, 'answers', index);
    const [, setItems] = useTaskField('items', itemIndex, 'answers');
    const [imageId, setImageId, imageError] = useField('image_id');
    const [isCorrect] = useField('is_correct_answer');

    const setAsCorrect = useCallback(() => {
        setItems(prev => prev.map((a, i) => ({
            ...a,
            is_correct_answer: i === index,
        })));
    }, [index, setItems]);

    return (
        <>
            {hasKeySelection &&
                <Checkbox
                    checked={isCorrect}
                    onClick={setAsCorrect}
                    icon={<CircleUnchecked/>}
                    checkedIcon={<CircleChecked/>}
                    color='primary'
                />
            }
            <ImagePicker
                label={isCorrect ? 'Key' : 'Distractor'}
                value={imageId}
                onChange={setImageId}
                fullWidth
                error={!!imageError}
                helperText={imageError ?? (hasKeySelection ? 'Select if key' : ' ')}
            />
        </>
    );
}


function PaletteSelector(disabled = true) {
    const [colors, setColors] = useTaskField('colors');

    const handleColourPicker = (color, index) => {
        let newPalette = clone(colors);
        newPalette[index] = color
        setColors(newPalette);
    }

    return (
        <>
            <Typography variant="h6" style={{ paddingTop: '12px', paddingBottom: '12px' }}>
                Color palette
            </Typography>
            <Grid container spacing={3} >
                {colors.map((colour, index) => {
                    return (
                        <Grid key={`column-${index}`} item xs={3}>
                            <ColourPicker
                                disabled={!!disabled}
                                colour={colour}
                                index={index}
                                onChange={handleColourPicker}
                            />
                        </Grid>
                    )
                })}
            </Grid>
        </>
    );
}

function ColorKeySelector({ colors, itemIndex, answerIndex }) {
    const [key, setKey, keyError] = useTaskField('items', itemIndex, 'answers', answerIndex, 'color_hex_value');

    const onKeyColorChange = useChangeEvent(setKey);

    // Will reset to color[0], in order to retain selection index it has to be kept as state.
    useEffect(() => {
        if (!colors.includes(key)) {
            setKey(colors[0]);
        }
    }, [colors]);

    return (
        <FormControl>
            <Select
                variant='filled'
                value={key || colors[0]}
                defaultValue={key || colors[0]}
                label="Key color"
                onChange={onKeyColorChange}
                error={!!keyError}
            >
                {colors.map((color, index) =>
                    <MenuItem key={index} value={color} selected={color === key}>
                        <ColourPicker
                            disabled
                            index={index}
                            colour={color}
                        />
                    </MenuItem>
                )}
            </Select>
            <FormHelperText style={{ alignSelf: 'center' }}>Key color</FormHelperText>
        </FormControl>
    );
}
